import React from 'react';
import { ChevronRight, Heart, Brain, Users, Shield, Activity, Clock, Baby, Pill, Sparkles, Zap, MessageCircle, Target, Eye, Sun, Moon, Cloud, AlertTriangle, Headphones, Smile, Frown } from 'lucide-react';

const AdultServices = () => {
  const adultServices = [
    {
      id: 1,
      title: "Anxiety Disorder",
      description: "Comprehensive treatment for various anxiety disorders, including generalized anxiety, panic disorder, and social anxiety, using evidence-based therapies.",
      icon: "/static/img/services/mental-health.png"
    },
    {
      id: 2,
      title: "Depression",
      description: "Specialized care for depression, including major depressive disorder, persistent depressive disorder, and seasonal affective disorder.",
      icon: "/static/img/services/self-confident.png"
    },
    {
      id: 3,
      title: "Family Counseling",
      description: "Therapeutic intervention to improve family dynamics, communication patterns, and resolve conflicts within family systems.",
      icon: "/static/img/services/mental-health.png"
    },
    {
      id: 4,
      title: "Bipolar Disorders",
      description: "Specialized treatment for bipolar I and II disorders, including mood stabilization and relapse prevention strategies.",
      icon: "/static/img/services/self-confident.png"
    },
    {
      id: 5,
      title: "Obsessive Compulsive Disorder",
      description: "Evidence-based treatment for OCD using Exposure and Response Prevention (ERP) therapy and medication management.",
      icon: "/static/img/services/mental-health.png"
    },
    {
      id: 6,
      title: "Anger Management",
      description: "Structured program to help individuals understand and manage anger through cognitive-behavioral techniques and skill building.",
      icon: "/static/img/services/self-confident.png"
    },
    {
      id: 7,
      title: "Personality Disorders",
      description: "Specialized therapy for personality disorders, including borderline, narcissistic, and avoidant personality disorders.",
      icon: "/static/img/services/mental-health.png"
    },
    {
      id: 8,
      title: "Behavioral Addictions",
      description: "Treatment for non-substance addictions, including gambling, internet, gaming, and shopping addictions.",
      icon: "/static/img/services/self-confident.png"
    },
    {
      id: 9,
      title: "Psychosis Problem",
      description: "Comprehensive care for psychotic disorders, including schizophrenia, schizoaffective disorder, and brief psychotic episodes.",
      icon: "/static/img/services/mental-health.png"
    },
    {
      id: 10,
      title: "LGBT Psychiatry",
      description: "Affirming mental health care for LGBTQ+ individuals addressing unique challenges including identity, discrimination, and coming out.",
      icon: "/static/img/services/self-confident.png"
    },
    {
      id: 11,
      title: "De-Addiction Counseling",
      description: "Comprehensive treatment for substance use disorders, including alcohol, opioids, stimulants, and other substances.",
      icon: "/static/img/services/mental-health.png"
    },
    {
      id: 12,
      title: "Geriatrics Mental Health",
      description: "Specialized mental health care for older adults addressing dementia, depression, anxiety, and age-related cognitive changes.",
      icon: "/static/img/services/self-confident.png"
    },
    {
      id: 13,
      title: "Self-Esteem Building",
      description: "Therapeutic interventions to improve self-worth, confidence, and develop a positive self-image through evidence-based techniques.",
      icon: "/static/img/services/mental-health.png"
    },
    {
      id: 14,
      title: "Work-Related Stress",
      description: "Specialized therapy for workplace stress, burnout, career transitions, and work-life balance issues.",
      icon: "/static/img/services/self-confident.png"
    },
    {
      id: 15,
      title: "Women's Mental Health",
      description: "Comprehensive mental health care addressing women-specific issues, including postpartum depression, menopause, and trauma.",
      icon: "/static/img/services/mental-health.png"
    },
    {
      id: 16,
      title: "Severe Psychiatric Disorders",
      description: "Intensive treatment for severe mental illnesses requires comprehensive care and ongoing management.",
      icon: "/static/img/services/self-confident.png"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="text-white py-32 relative overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-top"
          style={{ 
            backgroundImage: `url('/static/img/service-images/14.webp')` 
          }}
        />
        
        {/* Dark Overlay for better text readability */}
        <div className="absolute inset-0 bg-black bg-opacity-40" />
        
        {/* Content */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold leading-tight mb-6">
              Adult Mental Health Services
            </h1>
            <p className="text-lg text-white/80 max-w-3xl mx-auto">
              Comprehensive mental health care tailored for adults seeking support and healing
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {adultServices.map((service) => (
              <div key={service.id} className="relative bg-white rounded-3xl p-8 hover:bg-primary-50 hover:shadow-xl transition-all duration-300 group">
                {/* Top Left Icon */}
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center group-hover:bg-primary-200 transition-colors">
                    <img 
                      src={service.icon} 
                      alt="Service Icon"
                      className="w-10 h-10 object-contain"
                    />
                  </div>
                </div>
                
                {/* Title */}
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {service.title}
                </h3>
                
                {/* Description */}
                <p className="text-gray-600 text-base leading-relaxed">
                  {service.description}
                </p>
                
                {/* Arrow Button - Top Right, Shows on Hover */}
                <div className="absolute top-8 right-8 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button className="w-12 h-12 bg-gray-900 text-white rounded-full flex items-center justify-center hover:bg-gray-800 transition-colors">
                    <ChevronRight className="w-6 h-6" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div> 
  );
};

export default AdultServices;
